import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import type AbilitiesService from 'ember-can/services/abilities';
import type ConfigModel from 'garaje/models/config';
import type DeviceModel from 'garaje/models/device';
import type LocationModel from 'garaje/models/location';
import type LocationSubscriptionModel from 'garaje/models/location-subscription';
import type PrinterModel from 'garaje/models/printer';
import type SubscriptionModel from 'garaje/models/subscription';
import type DeviceStatusService from 'garaje/services/device-status-service';
import type LocationFeatureFlagsService from 'garaje/services/location-feature-flags';
import type StateService from 'garaje/services/state';
import type { RecordArray } from 'garaje/utils/type-utils';
import { hash } from 'rsvp';

export interface VisitorsDevicesIpadsRouteModel {
  config: ConfigModel;
  ipads: RecordArray<DeviceModel> | DeviceModel[];
  location: LocationModel;
  printers: RecordArray<PrinterModel>;
  subscription: LocationSubscriptionModel | SubscriptionModel;
}

export default class VisitorsDevicesIpadsRoute extends Route {
  @service declare abilities: AbilitiesService;
  @service declare deviceStatusService: DeviceStatusService;
  @service declare locationFeatureFlags: LocationFeatureFlagsService;
  @service declare router: RouterService;
  @service declare state: StateService;
  @service declare store: StoreService;

  titleToken = 'iPads';

  beforeModel(): void {
    if (this.abilities.cannot('visit devices')) {
      void this.router.transitionTo('unauthorized');
    }
  }

  async model(): Promise<VisitorsDevicesIpadsRouteModel> {
    const subscription = this.state.vrSubscription!;
    const location = this.state.currentLocation;
    const locationId = location.id;

    return hash({
      config: location.config,
      ipads: this.loadIpads(locationId),
      location,
      printers: this.store.query('printer', { filter: { location: locationId }, include: 'devices,location' }),
      subscription,
    });
  }

  private async loadIpads(locationId: string): Promise<RecordArray<DeviceModel> | DeviceModel[]> {
    if (this.locationFeatureFlags.isEnabled('devices-service-status-api')) {
      await this.deviceStatusService.ensureStatuses(locationId, 'visitors');
      const statuses = Array.from(this.deviceStatusService.statusByDeviceId.values());
      const devices = statuses
        .filter((s) => String(s.locationId) === String(locationId))
        .map((s) => {
          const normalized = this.store.normalize('device', {
            type: 'devices',
            id: s.externalId,
            attributes: {
              ...s.telemetry,
              'is-sleeping': s.telemetry?.['sleeping'] ?? false,
              'is-connected': s.isConnected,
              'last-heartbeat-at': s.connectionStatusUpdatedAt?.toISOString() ?? null,
            },
            relationships: {
              location: { data: { type: 'locations', id: locationId } },
            },
          });
          return this.store.push(normalized) as DeviceModel;
        })
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

      return devices;
    }

    return this.store.query('device', {
      filter: { location: locationId },
      sort: 'name',
      include: 'printer,failover-printer',
    });
  }
}
